import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AppKitAuthContext";
import { detectUserTokensCached } from "../utils/tokenBalanceDetection";
import styles from "../styles/InsufficientBalancePopup.module.css";

interface InsufficientBalancePopupProps {
  isOpen: boolean;
  onClose: () => void;
  requiredAmount: string;
  context?: 'bet' | 'draw';
}


/** for Embedded Wallet (normally non-crypto user)
 * Buy BNB + Receive BNB (to withdraw from CEX)
 * 
 * for Non-Embedded Wallet (normally crypto user)
 * Buy BNB + Swap Tokens / Bridge
**/
const InsufficientBalancePopup: React.FC<InsufficientBalancePopupProps> = ({
  isOpen,
  onClose,
  requiredAmount,
  context = 'bet',
}) => {

  const { openAccount, openOnRamp, openSwap, isEmbeddedWallet, walletState } = useAuth();
  const currentBalance = walletState.balance || "0";
  
  const [isCheckingTokens, setIsCheckingTokens] = useState(false);
  const [hasBEP20Tokens, setHasBEP20Tokens] = useState(false);
  const [tokenCheckComplete, setTokenCheckComplete] = useState(false);
  
  const hasGas = Number(currentBalance) > 0;
  
  // Define handler functions with useCallback to avoid re-creation
  const handleBuyCrypto = useCallback(() => {
    openOnRamp();
    onClose();
  }, [openOnRamp, onClose]);

  const handleSwapTokens = useCallback(() => {
    openSwap();
    onClose();
  }, [openSwap, onClose]);

  const handleReceiveTokens = useCallback(() => {
    openAccount();
    onClose();
  }, [openAccount, onClose]);
  
  const handleBridgeTokens = useCallback(() => {
    window.open("https://portalbridge.com/?targetChain=BSC&targetAsset=BNB", "_blank", "noopener");
    onClose();
  }, [onClose]);
  
  // Check for BEP20 tokens when popup opens
  useEffect(() => {
    if (isOpen && walletState?.address && !isEmbeddedWallet && !tokenCheckComplete) {
      setIsCheckingTokens(true);
      detectUserTokensCached(walletState.address)
        .then((result) => {
          setHasBEP20Tokens(result.hasTokens);
          setTokenCheckComplete(true);
        })
        .catch((error) => {
          console.error("Failed to check tokens:", error);
          setHasBEP20Tokens(false);
          setTokenCheckComplete(true);
        })
        .finally(() => {
          setIsCheckingTokens(false);
        });
    } else if (isEmbeddedWallet) {
      setTokenCheckComplete(true);
    }
  }, [isOpen, walletState?.address, isEmbeddedWallet, tokenCheckComplete]);
  
  // Reset state when popup closes
  useEffect(() => {
    if (!isOpen) {
      setTokenCheckComplete(false);
      setHasBEP20Tokens(false);
      setIsCheckingTokens(false);
    }
  }, [isOpen]);
  
  // Determine second option based on wallet type and token holdings
  const getSecondOption = () => {
    if (isEmbeddedWallet) {
      return { text: "Receive BNB", handler: handleReceiveTokens };
    }
    
    if (hasGas && hasBEP20Tokens) {
      return { text: "Swap Tokens", handler: handleSwapTokens };
    }
    
    return { text: "Bridge Tokens", handler: handleBridgeTokens };
  };
  
  const secondOption = getSecondOption();
  
  // Dynamic message based on options
  const getMessage = () => {
    const baseMessage = context === 'draw' 
      ? "You need more BNB to participate in the daily draw." 
      : "You need more BNB to place this bet.";
    
    if (isEmbeddedWallet) {
      return `${baseMessage} You can buy BNB directly or receive BNB from an exchange.`;
    }
    
    if (hasGas && hasBEP20Tokens) {
      return `${baseMessage} You can buy BNB directly or swap your existing tokens.`;
    }
    
    return `${baseMessage} You can buy BNB directly or bridge tokens from other chains.`;
  };

  if (!isOpen) return null;

  return (
    <div className={styles.insufficientBalanceOverlay}>
      <div className={styles.insufficientBalancePopup}>
        <div className={styles.insufficientBalanceHeader}>
          <h2>Insufficient Balance</h2>
          <button onClick={onClose} className={styles.insufficientBalanceClose} type="button">
            ×
          </button>
        </div>

        <div className={styles.insufficientBalanceContent}>
          <div className={styles.balanceInfo}>
            <div className={styles.balanceRow}>
              <span className={styles.label}>Required:</span>
              <span className={`${styles.amount} ${styles.required}`}>{requiredAmount} BNB</span>
            </div>
            <div className={styles.balanceRow}>
              <span className={styles.label}>Current:</span>
              <span className={`${styles.amount} ${styles.current}`}>{Number(currentBalance).toFixed(4)} BNB</span>
            </div>
          </div>

          <div className={styles.balanceMessage}>
            {getMessage()}
          </div>

          <div className={styles.balanceActions}>
            <button onClick={handleBuyCrypto} className={`${styles.balanceActionBtn} ${styles.primary}`} type="button">
              Buy BNB
            </button>
            <button 
              onClick={secondOption.handler} 
              className={`${styles.balanceActionBtn} ${styles.secondary}`} 
              type="button"
              disabled={isCheckingTokens && !isEmbeddedWallet}
            >
              {isCheckingTokens && !isEmbeddedWallet ? "Checking..." : secondOption.text}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsufficientBalancePopup;
